import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {timeUtil} from '@/helper/time';
import {gt, numUtil} from '@/helper/num';
import {qtyUtil} from '@/helper/qty-type-contract';
import {ptConfig$} from '@/config';
import {contractFillOrdersCache$} from './orders-fill-contract-cache';
import {contractsCache$} from '@/config';
import {fiatRate$} from '@/state/fiat-rate';
import {indexPricesCache$} from '@/price-index/price-index';
import {isTrailingStopOrder} from '@/tpslts/order-filters';
import type {ws, ContractFillOrder, ContractPair, CurrencyEntity, FiatEntity} from '@/types';
type ContractOrderRaw = ws.ContractOrderRaw;

export type Cache = Map<string, ContractOrderRaw>;

export const contractFillOrders$ = combineLatest([
  contractFillOrdersCache$,
  contractsCache$,
  ptConfig$,
  indexPricesCache$,
  fiatRate$,
]).pipe(
  map(([cache, contractsCache, ptConfig, indexPricesCache, fiatRate]): ContractFillOrder[] =>
    cache
      .toArray((o) => <ContractFillOrder>convertToFillOrder(o, contractsCache, ptConfig, indexPricesCache, fiatRate))
      .filter(Boolean)
  )
);

interface FillOrderRow {
  ordStatus: 'New' | 'PartiallyFilled' | 'Untriggered' | 'Filled' | 'Deactivated' | 'Canceled' | 'Rejected';
  orderQtyStr: string;
  symbol: string;
  side: string;
  orderID: string;
  closedPnl: string;
  execFeeMoney: string; // 将费用折算成统一的金额, 目的是单位统一，进行排序！
  execFee: string;
  priceEp: number;
  execQtyStr: string;
  execID: string;
  execPriceEp: number;
  displaySymbol: string;
  transactTimestamp: number;
  clOrdID: string;
  price: string;
  execPrice: string;
  transactTime: string;
  orderQty: number;
  execQty: number;
  timeInForce: 'PostOnly' | 'GoodTillCancel' | 'ImmediateOrCancel' | 'FillOrKill' | '';
  orderListStatus: 'UNSPECIFIED' | 'EXECUTING' | 'ALL_DONE' | 'REJECT'; // UNSPECIFIED: 非order list， EXECUTING: list已接收，ALL_DONE: list已经终态（两个订单都closed），REJECT: order list被拒绝（两个订单都拒绝）
  orderListType: string; // 0默认值: 默认值，非OCO订单； 1为OCO订单
  orderListId: string; // id相同的订单为同一个OCO的legs
  ordType:
    | 'BoSlLimit'
    | 'BoSlMarket'
    | 'BoTpLimit'
    | 'Bracket'
    | 'Limit'
    | 'LimitIfTouched'
    | 'Market'
    | 'MarketIfTouched'
    | 'Stop'
    | 'StopLimit'
    | 'Liquidation'
    | 'ADL';
}

function convertToFillOrder(
  raw: ContractOrderRaw,
  contractsCache: Map<string, ContractPair>,
  ptConfig: CurrencyEntity,
  indexPricesCache,
  fiatRate
): FillOrderRow & {
  orderQtyByCont: string;
  orderQtyByValue: string;
  orderQtyByCoin: string;
  execQtyByCont: string;
  execQtyByValue: string;
  execQtyByCoin: string;
  posSide: string;
  displayOrdType: string;
} {
  const {
    orderID,
    clOrdID,
    ordType,
    ordStatus,
    symbol,
    side,
    orderQty,
    priceEp,
    execID,
    execQty,
    execPriceEp,
    transactTimeNs,
    timeInForce,
    closedPnlEv,
    execFeeEv,
    ptFeeEv,
    tradeType,
    posSide,
    pegPriceType,
    orderListStatus,
    orderListType,
    orderListId,
  } = raw as any;
  const contract = contractsCache.get(symbol);
  if (!contract) {
    return null;
  }
  const {displaySymbol, pricePrecision, priceScale, valuePrecision, valueScale, baseCurrency, settleCurrencyDisplay} =
    contract;
  const transactTimestamp = Math.floor(transactTimeNs / 1e6);

  const price = numUtil.floor(priceEp, pricePrecision, priceScale);
  const execPrice = numUtil.floor(execPriceEp, pricePrecision, priceScale);

  const execFee = execFeeEv ? numUtil.floor(execFeeEv, valuePrecision, valueScale) + ' ' + settleCurrencyDisplay : '';
  const hasPtFee = ptFeeEv && gt(ptFeeEv, 0);
  const ptFee = hasPtFee ? numUtil.floor(ptFeeEv, ptConfig.valuePrecision) + ' ' + ptConfig.displayCurrency : '';

  const item = hasPtFee ? indexPricesCache.get('.PT') : indexPricesCache.get('.' + baseCurrency);
  const indexPriceEp = item ? item.priceEp : 0;
  const finallyFeeEv = (hasPtFee ? ptFeeEv : execFeeEv) || 0;
  const ordType2 = genOrdType(ordType, tradeType);

  return {
    orderID,
    clOrdID,
    ordType: ordType2,
    displayOrdType: isTrailingStopOrder(pegPriceType, ordType) ? 'TrailingStop' : ordType2,
    ordStatus,
    symbol,
    displaySymbol,
    side,
    orderQty: Number(orderQty),
    orderQtyByCont: qtyUtil.toCont(contract, orderQty),
    orderQtyByValue: qtyUtil.toValue(contract, orderQty, price),
    orderQtyByCoin: qtyUtil.toCoin(contract, orderQty, price),
    orderQtyStr: numUtil.delimit(orderQty),
    priceEp: Number(priceEp),
    price: numUtil.delimit(price),
    execID,
    execQty,
    execQtyByCont: qtyUtil.toCont(contract, execQty),
    execQtyByValue: qtyUtil.toValue(contract, execQty, execPrice),
    execQtyByCoin: qtyUtil.toCoin(contract, execQty, execPrice),
    execQtyStr: numUtil.delimit(execQty),
    execPriceEp,
    execPrice: numUtil.delimit(execPrice),
    transactTimestamp,
    transactTime: timeUtil.formatDateTime(transactTimestamp),
    timeInForce,
    closedPnl: closedPnlEv ? numUtil.floor(closedPnlEv, valuePrecision, valueScale) + ' ' + settleCurrencyDisplay : '-',
    execFee: ptFee || execFee || '-',
    execFeeMoney: calcFiat(contract, fiatRate, finallyFeeEv, indexPriceEp),
    posSide,
    orderListStatus,
    orderListType,
    orderListId,
  };
}

function genOrdType(ordType, tradeType) {
  if (ordType) {
    return ordType;
  }
  return (
    {
      AdlTrade: 'AdlTrade',
      LiqTrade: 'Liquidation',
    }[tradeType] || '-'
  );
}

// 换算之后仅用来排序，使用更高精度8
function calcFiat(contract: ContractPair, fiatEntity: FiatEntity, valueEv: number, indexPriceEp: number): string {
  if (!fiatEntity) {
    return '';
  }
  const {contractSide, valueScale, isSupportHedge} = contract;
  //新币对value是真实值，而法币都是放大的值，故将value放大之后再进行计算
  const priceFactor = isSupportHedge ? 1e4 : 1;
  const fiatValueEv = Big(valueEv).times(priceFactor).div(fiatEntity.priceEp);
  if (contractSide > 0) {
    return numUtil.floor(fiatValueEv, 8);
  }
  return numUtil.floor(Big(fiatValueEv).times(indexPriceEp), 8, valueScale);
}
